import CodeMirror from 'codemirror';
import { includesIgnoreCase } from '../utils';
import * as DeclarationMatch from '../../ast/declarations';
import { DeclarationFactory } from '../../ast/declarations/base';
import { matchFqnPath } from '../../utils/fqn';
import { operatorsMap } from './../config';
import { matchModel, matchModelPath } from './model';
import { IModelField, IPlaceholder, IProxy, IFunctionObject, IDeclarationObject } from '../index.d';



interface IModeConfig {
  name: string;
  fqn?: IModelField;
  functions: IFunctionObject[];
  declarations: IDeclarationObject[];

  // placeholders 可能为动态的值，所以用函数接收
  getPlaceholders(): IPlaceholder[];
  proxies: IProxy[];
}

export default function defineMode(config: IModeConfig) {

  CodeMirror.defineMode(name, () => {
    const fqn = config.fqn;
    const proxies = config.proxies || [];
    const functions = config.functions;
    const functionCodes = functions.map(item => item.code);
    const declarations = config.declarations;
    const declarationCodes = declarations.map(item => item.code);
    const operatorChars = /^[\/*+\-%<>!=&|~^]/;
    const brackets = /^[\{}\(\)\[\]]/;

    const paths: string[] = [];

    function tokenBase(stream: CodeMirror.StringStream, state: any) {
      const placeholders = config.getPlaceholders();
      state.line = (stream as any).lineOracle.line;

      // 匹配显示类型声明
      const proxyToken = matchProxyToken(stream, proxies, state);
      if (proxyToken) {
        return proxyToken;
      }

      // 匹配显示类型声明
      const declarationToken = matchDeclarationToken(stream, declarations);
      if (declarationToken) {
        return declarationToken;
      }

      const modelToken = matchModel(stream);
      if (modelToken) {
        return modelToken;
      }
      const modelPathToken = matchModelPath(stream);
      if (modelPathToken) {
        return modelPathToken;
      }

      const operatorToken = matchOperatorToken(stream);
      if (operatorToken) {
        return operatorToken;
      }

      const atomToken = matchAtomToken(stream);
      if (atomToken) {
        return atomToken;
      }

      // {-1000}和{1000}表示为属性ID
      if (stream.match(/^\{-?\d+\}/)) {
        return 'custom-property-id';
      }

      for (const placeholder of placeholders) {
        if (stream.match(placeholder.regexp, false)) {
          stream.match(placeholder.regexp);
          state.placeholder = placeholder;
          return `placeholder ${placeholder.type}`;
        }
      }

      const ch = stream.next();


      if (!ch) { return null; }

      if (ch === ',') {
        paths.length = 0;
        return 'comma';
      }


      if (ch.charCodeAt(0) > 47 && ch.charCodeAt(0) < 58) {
        // numbers
        stream.match(/^[0-9]*(\.[0-9]+)?([eE][-+]?[0-9]+)?/);
        stream.match(/^\.(?!\.)/);
        paths.length = 0;
        return 'number';
      } else if (ch === "'" || ch === '"') {
        // strings
        state.tokenize = tokenLiteral(ch);
        return state.tokenize(stream, state);
      } else if (brackets.test(ch)) {
        // brackets
        paths.length = 0;
        return 'bracket';

      } else if (ch === '.') {
        // .1 for 0.1
        if (stream.match(/^(?:\d+(?:e[+-]?\d+)?)/i)) {
          paths.length = 0;
          return 'number';
        }

        if (stream.match(/^\.+/)) {
          return null;
        }
        return 'dot';
      } else if (operatorChars.test(ch)) {
        // operators
        stream.eatWhile(operatorChars);
        paths.length = 0;
        return 'operator';
      } else {

        stream.eatWhile(/^[_\w\d\[\]$\xa1-\uffff]/);
        const word = stream.current();

        if (fqn) {
          if (word === 'this') {
            paths.length = 0;
            paths.push('this');
            return 'fqn root property path::this';
          }

          if (paths[0] === 'this') {
            paths.push(word);
          }

          if (paths.length > 0 && matchFqnPath(fqn, paths.join('.'))) {
            return 'fqn property path::' + paths.join('.');
          }
        }

        paths.length = 0;
        if (includesIgnoreCase(functionCodes, word)) {
          return 'function';
        }
        if (declarationCodes.includes(word)) {
          return 'declaration';
        }
        return null;
      }
    }

    // 'string', with char specified in quote escaped by '\'
    function tokenLiteral(quote: string) {
      return (stream: CodeMirror.StringStream, state: any) => {
        let escaped = false;
        let ch = stream.next();
        while (ch) {
          if (ch === quote && !escaped) {
            state.tokenize = tokenBase;
            break;
          }
          escaped = !escaped && ch === '\\';
          ch = stream.next();
        }
        return 'string';
      };
    }


    function pushContext(
      stream: CodeMirror.StringStream,
      state: any,
      type: string
    ) {
      state.context = {
        prev: state.context,
        indent: stream.indentation(),
        col: stream.column(),
        type
      };
    }

    function popContext(state: any) {
      state.indent = state.context.indent;
      state.context = state.context.prev;
    }

    return {
      startState() {
        return { tokenize: tokenBase, context: null };
      },

      token(stream, state) {
        if (stream.sol()) {
          if (state.context && state.context.align === null) {
            state.context.align = false;
          }
        }
        if (state.tokenize === tokenBase && stream.eatSpace()) {
          return null;
        }

        const style = state.tokenize(stream, state);

        if (state.context && state.context.align === null) {
          state.context.align = true;
        }


        const tok = stream.current();
        if (tok === '(') {
          pushContext(stream, state, ')');
        } else if (tok === '[') {
          pushContext(stream, state, ']');
        } else if (state.context && state.context.type === tok) {
          popContext(state);
        }

        return style;
      },
      closeBrackets: '()[]{}\'\'""``'
    };
  });
}

function matchProxyToken(stream: CodeMirror.StringStream, proxies: IProxy[], state: any) {
  for (const proxy of proxies) {
    if (stream.match(proxy.editorTokenMatch, false)) {
      if (stream.match(proxy.editorTokenMatch)) {
        state.proxy = proxy;
        return `proxy`;
      }
    }
  }
}


function matchDeclarationToken(stream: CodeMirror.StringStream, declarations: IDeclarationObject[]) {
  // CURRENT_MEMBER_POINT、CURRENT_MEMBER_GRADE 使用 Declaration 的方式生成 Token
  for (const declaration of declarations.concat([
    { code: 'CURRENT_MEMBER_POINT', name: 'CURRENT_MEMBER_POINT' },
    { code: 'CURRENT_MEMBER_GRADE', name: 'CURRENT_MEMBER_GRADE' }
  ])) {
    const match = (DeclarationMatch as any)[declaration.code] as DeclarationFactory;
    if (match && match.generateRegExp) {
      if (stream.match(match.generateRegExp())) {
        return `declaration ${match.declaration}`;
      }
    }
  }
}


function matchOperatorToken(stream: CodeMirror.StringStream) {
  for (const operator of operatorsMap) {
    if (stream.match(operator.matchRegexp, false)) {
      stream.match(operator.eatRegexp);
      return operator.type;
    }
  }
}

// true,false,null,asc,desc
function matchAtomToken(stream: CodeMirror.StringStream) {
  if (stream.match(/^true(\W|$)/i, false)) {
    stream.match(/^true/i);
    return 'atom Boolean';
  }
  if (stream.match(/^false(\W|$)/i, false)) {
    stream.match(/^false/i);
    return 'atom Boolean';
  }
  if (stream.match(/^null(\W|$)/i, false)) {
    stream.match(/^null/i);
    return 'atom Null';
  }

  if (stream.match(/^asc(\W|$)/i, false)) {
    stream.match(/^asc/i);
    return 'atom Direction ASC';
  }

  if (stream.match(/^desc(\W|$)/i, false)) {
    stream.match(/^desc/i);
    return 'atom Direction DESC';
  }
}

